const getTarget = (root, id) => root.querySelector(`#${CSS.escape(id)}`);

const closeMenu = (root, restoreFocus = false) => {
  root.querySelectorAll('[data-tg-menu-panel]').forEach((panel) => {
    if (panel.hidden) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.hidden = true;
      return;
    }

    panel.addEventListener(
      'animationend',
      () => {
        if (panel.hasAttribute('data-tg-menu-closing')) {
          panel.hidden = true;
          panel.removeAttribute('data-tg-menu-closing');
        }
      },
      { once: true }
    );
    panel.setAttribute('data-tg-menu-closing', '');
  });
  root.querySelectorAll('[data-tg-menu-toggle]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', 'false');
  });
  root.querySelectorAll('[data-tg-menu-panel]').forEach((panel) => {
    panel.removeAttribute('data-tg-menu-third-level-open');
    panel.querySelectorAll('[data-tg-menu-select]').forEach((item) => {
      item.classList.remove('tg-menu__item--selected');
      item.setAttribute('aria-expanded', 'false');
    });
  });
  root.removeAttribute('data-tg-menu-open');

  if (restoreFocus) {
    root.querySelector('[data-tg-menu-toggle][data-tg-menu-last-open]')?.focus();
  }
  root.querySelectorAll('[data-tg-menu-last-open]').forEach((toggle) => {
    toggle.removeAttribute('data-tg-menu-last-open');
  });
};

const selectMenuItem = (root, button) => {
  const panel = button.closest('[data-tg-menu-panel]');
  const targetId = button.dataset.tgMenuSelect;
  const target = panel && targetId ? getTarget(panel, targetId) : null;

  if (!panel || !target) return;

  panel.querySelectorAll('[data-tg-menu-detail]').forEach((detail) => {
    detail.hidden = true;
  });

  const visibleDetails = new Set([target, button.closest('[data-tg-menu-detail]')]);
  visibleDetails.forEach((detail) => {
    let currentDetail = detail;
    while (currentDetail && currentDetail !== panel) {
      currentDetail.hidden = false;
      currentDetail = currentDetail.parentElement?.closest('[data-tg-menu-detail]') ?? null;
    }
  });

  if (button.closest('.tg-menu__level--secondary')) {
    const primaryId = targetId.split('-child-')[0];
    panel.querySelector(`#${CSS.escape(primaryId)}`)?.removeAttribute('hidden');
    panel.setAttribute('data-tg-menu-third-level-open', '');
    target.querySelector('a, button')?.focus();
  } else if (button.closest('.tg-menu__level--primary')) {
    panel.removeAttribute('data-tg-menu-third-level-open');
  }
  panel.querySelectorAll('[data-tg-menu-select]').forEach((item) => {
    item.classList.remove('tg-menu__item--selected');
    item.setAttribute('aria-expanded', 'false');
  });
  button.classList.add('tg-menu__item--selected');
  button.setAttribute('aria-expanded', 'true');
  let parentDetail = button.closest('[data-tg-menu-detail]');
  while (parentDetail) {
    const parentButton = panel.querySelector(`[data-tg-menu-select="${CSS.escape(parentDetail.id)}"]`);
    parentButton?.classList.add('tg-menu__item--selected');
    parentButton?.setAttribute('aria-expanded', 'true');
    parentDetail = parentDetail.parentElement?.closest('[data-tg-menu-detail]') ?? null;
  }
  const image = panel.querySelector(`[data-tg-menu-image="${CSS.escape(targetId)}"]`);
  if (image) {
    panel.querySelectorAll('[data-tg-menu-image]').forEach((item) => {
      item.hidden = item !== image;
    });
  }
};

const openMenu = (root, toggle) => {
  const panel = getTarget(root, toggle.dataset.tgMenuToggle);
  if (!panel) return;

  const openToggle = root.querySelector('[data-tg-menu-toggle][aria-expanded="true"]');
  if (openToggle && openToggle !== toggle) {
    openToggle.removeAttribute('data-tg-menu-last-open');
  }

  root.querySelectorAll('[data-tg-menu-panel]').forEach((item) => {
    item.removeAttribute('data-tg-menu-closing');
    item.hidden = item !== panel;
    item.removeAttribute('data-tg-menu-third-level-open');
    item.querySelectorAll('[data-tg-menu-detail]').forEach((detail) => {
      detail.hidden = true;
    });
    item.querySelectorAll('[data-tg-menu-select]').forEach((select) => {
      select.classList.remove('tg-menu__item--selected');
      select.setAttribute('aria-expanded', 'false');
    });
    item.querySelectorAll('[data-tg-menu-image]').forEach((image) => {
      image.hidden = true;
    });
  });
  const firstPrimarySelect = panel.querySelector('.tg-menu__level--primary [data-tg-menu-select]');
  const firstDetail = firstPrimarySelect && getTarget(panel, firstPrimarySelect.dataset.tgMenuSelect);
  firstPrimarySelect?.classList.add('tg-menu__item--selected');
  firstPrimarySelect?.setAttribute('aria-expanded', 'true');
  firstDetail?.removeAttribute('hidden');
  panel.querySelectorAll('[data-tg-menu-image]').forEach((image, index) => {
    image.hidden = index !== 0;
  });
  root.querySelectorAll('[data-tg-menu-toggle]').forEach((item) => {
    item.setAttribute('aria-expanded', String(item === toggle));
  });
  toggle.setAttribute('data-tg-menu-last-open', '');
  root.setAttribute('data-tg-menu-open', '');
};

const initializeMenu = (root) => {
  if (root.dataset.tgMenuInitialized) return;
  root.dataset.tgMenuInitialized = 'true';

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-tg-menu-toggle]');
    if (toggle) {
      event.preventDefault();
      if (toggle.getAttribute('aria-expanded') === 'true') {
        closeMenu(root);
      } else {
        openMenu(root, toggle);
      }
      return;
    }

    const item = target.closest('.tg-menu__level--primary [data-tg-menu-select]');
    if (item) {
      event.preventDefault();
      selectMenuItem(root, item);
    }
  });

  root.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const item = target.closest('[data-tg-menu-select]');
    if (item) selectMenuItem(root, item);
  });

  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenu(root, true);
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target instanceof Node && !root.contains(event.target)) {
      closeMenu(root);
    }
  });
};

const initializeMenus = () => {
  document.querySelectorAll('[data-tg-header-menu]').forEach(initializeMenu);
};

initializeMenus();
document.addEventListener('shopify:section:load', initializeMenus);

const menuObserver = new MutationObserver((mutations) => {
  mutations.forEach(({ addedNodes }) => {
    addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('[data-tg-header-menu]')) initializeMenu(node);
      node.querySelectorAll('[data-tg-header-menu]').forEach(initializeMenu);
    });
  });
});

menuObserver.observe(document.documentElement, { childList: true, subtree: true });
